import { apiGet, apiSend } from "./client.ts";

export type CameraPreset = {
  id: string;
  camera_id: string;
  grid_id: number;
  preset_id: string;
  preset_name: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

type PtzPresetListResponse = {
  camera_id: string;
  count: number;
  presets: CameraPreset[];
};

type PtzPresetRow = Partial<CameraPreset> & {
  preset_id?: string | number;
};

function unwrapEnvelope<T>(body: unknown): T {
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function toPresetRow(raw: PtzPresetRow): CameraPreset | null {
  const id = String(raw.id ?? "").trim();
  const cameraId = String(raw.camera_id ?? "").trim();
  const presetId = String(raw.preset_id ?? "").trim();
  const gridId = Number(raw.grid_id);
  if (!id || !cameraId || !presetId || !Number.isFinite(gridId)) {
    return null;
  }

  return {
    id,
    camera_id: cameraId,
    grid_id: gridId,
    preset_id: presetId,
    preset_name: String(raw.preset_name ?? "").trim(),
    created_by: raw.created_by ? String(raw.created_by) : null,
    created_at: String(raw.created_at ?? new Date().toISOString()),
    updated_at: String(raw.updated_at ?? new Date().toISOString()),
  };
}

function presetsPath(cameraId?: string): string {
  if (!cameraId) {
    return "/api/cameras/ptz/presets";
  }
  return `/api/cameras/${encodeURIComponent(cameraId)}/ptz/presets`;
}

export async function fetchCameraPresets(cameraId: string): Promise<CameraPreset[]> {
  const normalizedCameraId = String(cameraId || "").trim();
  if (!normalizedCameraId) return [];
  const body = await apiGet<unknown>(presetsPath(normalizedCameraId));
  const payload = unwrapEnvelope<PtzPresetListResponse | { presets?: PtzPresetRow[] }>(body);
  const list = Array.isArray((payload as PtzPresetListResponse).presets)
    ? (payload as PtzPresetListResponse).presets
    : [];

  return list
    .map((row) => toPresetRow(row))
    .filter((row): row is CameraPreset => row != null);
}

export async function saveCameraPreset(input: {
  cameraid: string;
  presetid: number;
  gridid: number;
  profileToken?: string;
}): Promise<CameraPreset> {
  const body = await apiSend<unknown>(presetsPath(), "POST", input);
  const payload = unwrapEnvelope<PtzPresetRow>(body);
  const row = toPresetRow(payload);
  if (!row) {
    throw new Error("Preset saved but response was invalid.");
  }
  return row;
}

export async function callCameraPreset(input: {
  cameraid: string;
  gridid?: number;
  presetToken?: string;
  presetName?: string;
  profileToken?: string;
  speed?: { x?: number; y?: number; zoom?: number };
}): Promise<{
  camera_id: string;
  grid_id: number | null;
  preset_id: string;
  preset_name: string | null;
  preset_token: string;
  profile_token: string;
}> {
  const body = await apiSend<unknown>("/api/cameras/ptz/presets/call", "POST", input);
  return unwrapEnvelope<{
    camera_id: string;
    grid_id: number | null;
    preset_id: string;
    preset_name: string | null;
    preset_token: string;
    profile_token: string;
  }>(body);
}
